import { AuthorType, ChatServiceType, IChatMessage } from '@nimbus-global/features/chat/models/chat.model';

// WhatsApp delivers a multi-attachment upload within a couple of seconds; anything further apart isn't the same burst.
const MAX_BURST_GAP_MS = 10_000;

function claimNearestEchoForEachMedia(
  media: IChatMessage['media'],
  pendingEchoIndexesByContent: Map<string, number[]>,
  isEcho: boolean[],
): void {
  for (const item of media ?? []) {
    const pendingIndexes = item.filename ? pendingEchoIndexesByContent.get(item.filename) : undefined;
    const nearestEchoIndex = pendingIndexes?.pop();
    if (nearestEchoIndex !== undefined) {
      isEcho[nearestEchoIndex] = true;
    }
  }
}

function rememberAsPossibleEcho(content: string, index: number, pendingEchoIndexesByContent: Map<string, number[]>): void {
  const pendingIndexes = pendingEchoIndexesByContent.get(content) ?? [];
  pendingIndexes.push(index);
  pendingEchoIndexesByContent.set(content, pendingIndexes);
}

// A media message claims the nearest matching text message as its echo, so a coincidental repeat isn't swept up too.
function computeEchoFlags(messages: IChatMessage[]): boolean[] {
  const isEcho = new Array<boolean>(messages.length).fill(false);
  // Pending text-message indexes per content, within the current burst; last entry = nearest.
  const pendingEchoIndexesByContent = new Map<string, number[]>();
  let lastCustomerMessageTime: number | null = null;

  for (let index = 0; index < messages.length; index++) {
    const message = messages[index];

    if (message.authorType !== AuthorType.Customer) {
      pendingEchoIndexesByContent.clear();
      lastCustomerMessageTime = null;
      continue;
    }

    const messageTime = new Date(message.dateTime).getTime();
    if (lastCustomerMessageTime !== null && messageTime - lastCustomerMessageTime > MAX_BURST_GAP_MS) {
      pendingEchoIndexesByContent.clear();
    }
    lastCustomerMessageTime = messageTime;

    if (message.media?.length) {
      claimNearestEchoForEachMedia(message.media, pendingEchoIndexesByContent, isEcho);
      continue;
    }

    rememberAsPossibleEcho(message.content, index, pendingEchoIndexesByContent);
  }

  return isEcho;
}

// WhatsApp delivers an inbound file as a text-only echo of the filename, then the real media message - drop the echo.
export function removeMediaFilenameEchoes(messages: IChatMessage[], chatType: ChatServiceType): IChatMessage[] {
  if (chatType !== ChatServiceType.Whatsapp) {
    return messages;
  }
  const isEcho = computeEchoFlags(messages);
  const result: IChatMessage[] = [];
  // Carries the day-separator flag across any run of dropped echoes, not just one.
  let carryFirstOfTheDay = false;
  for (let index = 0; index < messages.length; index++) {
    const message = messages[index];
    if (isEcho[index]) {
      carryFirstOfTheDay = carryFirstOfTheDay || message.isFirstOfTheDay;
      continue;
    }
    result.push(carryFirstOfTheDay ? { ...message, isFirstOfTheDay: true } : message);
    carryFirstOfTheDay = false;
  }
  return result;
}
